import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import ChannelIcon from './ChannelIcon';

const Notification = () => {
  const { notifications, removeNotification } = useNotification();
  const navigate = useNavigate();
  
  if (notifications.length === 0) {
    return null;
  }

  const formatTime = (value) => {
    const ts = value ? new Date(value).getTime() : null;
    const d = ts && Number.isFinite(ts) ? new Date(ts) : new Date();
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessageNotification = (notification) => {
    const data = notification.payload || {};
    const conversationId = data?.conversation?.id;
    const clientId = data?.client?.id;

    const clientName = data?.client?.name || 'Nouveau message';
    const orgName = data?.organization?.name || '';
    const conversationName = data?.conversation?.name || '';
    const agentName = data?.agent?.name || '';
    const preview = data?.message?.preview || '';
    const mode = data?.conversation?.mode || '';
    const time = formatTime(data?.created_at || notification.timestamp);

    // Bulle groupee : plusieurs messages sont arrives pendant qu'elle etait
    // affichee. On annonce le total et on garde l'apercu du dernier, pour que
    // l'agent sache combien de demandes l'attendent sans empiler les bulles.
    const groupCount = Number(data?.groupCount) || 1;
    const groupConversations = Number(data?.groupConversations) || 1;
    const groupLabel = groupCount > 1
      ? (groupConversations > 1
        ? `${groupCount} nouveaux messages · ${groupConversations} conversations`
        : `${groupCount} nouveaux messages`)
      : null;

    const canNavigate = !!(clientId && conversationId);

    const handleOpen = () => {
      if (canNavigate) {
        navigate(`/conversations/live/${clientId}/${conversationId}`);
      }
      removeNotification(notification.id);
    };

    return (
      <div
        key={notification.id}
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? handleOpen() : null)}
        className="flex items-start gap-3 px-3 py-2 rounded-xl shadow-lg border bg-white/95 border-slate-200 cursor-pointer backdrop-blur max-w-sm hover:bg-white transition-colors"
      >
        <div className="mt-0.5 flex items-center justify-center w-9 h-9 rounded-full bg-slate-900 text-white shrink-0">
          <ChannelIcon channel={data?.channel} size={18} className="opacity-95" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {clientName}
              {orgName ? <span className="font-normal text-slate-500"> - {orgName}</span> : null}
            </p>
            <span className="text-[11px] text-slate-500 shrink-0">{time}</span>
          </div>

          {groupLabel ? (
            <p className="text-[12px] font-semibold text-slate-700">{groupLabel}</p>
          ) : null}

          {(conversationName || agentName || mode) ? (
            <p className="text-[12px] text-slate-500 truncate">
              {conversationName ? conversationName : null}
              {agentName ? <span> - {agentName}</span> : null}
              {mode ? <span> - {mode === 'IA' ? 'IA' : 'Humain'}</span> : null}
            </p>
          ) : null}

          <p className="text-[13px] text-slate-800 truncate">{preview}</p>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            removeNotification(notification.id);
          }}
          className="ml-1 text-slate-400 hover:text-slate-600 focus:outline-none"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    );
  };
  
  return (
    <div className="fixed right-0 top-0 z-50 p-4 space-y-4 max-w-md">
      {notifications.map((notification) => (
        notification.type === 'message' ? (
          renderMessageNotification(notification)
        ) : (
        <div
          key={notification.id}
          className={`flex items-center justify-between px-4 py-3 rounded shadow-lg transform transition-all duration-500 ease-in-out ${
            notification.type === 'success'
              ? 'bg-green-50 border-l-4 border-green-500 text-green-700'
              : notification.type === 'error'
              ? 'bg-red-50 border-l-4 border-red-500 text-red-700'
              : notification.type === 'warning'
              ? 'bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700'
              : 'bg-blue-50 border-l-4 border-blue-500 text-blue-700'
          }`}
        >
          <div className="flex items-center">
            <span className="mr-2">
              {notification.type === 'success' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {notification.type === 'warning' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {notification.type === 'info' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </span>
            <p className="text-sm font-medium">{notification.message}</p>
          </div>
          <button
            onClick={() => removeNotification(notification.id)}
            className="ml-4 text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        )
      ))}
    </div>
  );
};

export default Notification;
